import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Search, X, ChevronRight, ChevronDown, List, Target, Calendar, Edit3, Check, Save, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { EventSummaryCard } from '@/components/events/EventSummaryCard';
import { eventTemplateService } from '@/lib/api/eventTemplate.service';
import { activityService } from '@/lib/api/activity.service';
import type { EventTemplateWithComponents, ExistingCelebrationEvent } from '@/types/eventTemplate.types';

interface Props {
  open: boolean;
  onClose: () => void;
  entityType: 'event' | 'celebration';
  entityId: number;
  existingEvents?: ExistingCelebrationEvent[];
  manualEntryUrl?: string;
  onManualEntry?: () => void;
  onApplied: (result: { activities_created: number; goals_attached: number; events_created?: number }) => void;
}

type ExpandSection = 'all' | 'activities' | 'goals';

const MAIN_ACTIVITY_TEMPLATE = {
  id: -1,
  title: 'Main Activity',
  description: 'Generic placeholder activity that can be added to any event.',
};

interface ActivityTemplateOption {
  id: number;
  title: string;
  description?: string;
  sourceTemplateName?: string;
}

export const EventTemplatePicker: React.FC<Props> = ({
  open,
  onClose,
  entityType,
  entityId,
  existingEvents = [],
  manualEntryUrl,
  onManualEntry,
  onApplied,
}) => {
  const { t } = useTranslation('events');
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EventTemplateWithComponents[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyRelated, setOnlyRelated] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<number, ExpandSection | null>>({});
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [addedTemplateIds, setAddedTemplateIds] = useState<Set<number>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = onlyRelated
        ? await eventTemplateService.getSuggestions(entityId, entityType)
        : await eventTemplateService.getAll();
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [onlyRelated, entityId, entityType]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setExpandedSections({});
      setApplyError(null);
      setAddedTemplateIds(new Set());
      setSuccessMessage(null);
      setOnlyRelated(true);
    }
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, [open]);

  // Load templates when modal opens or filter changes
  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open, loadTemplates]);

  // Normalize existing event titles for matching
  const existingTitlesLower = existingEvents.map(e => e.title.toLowerCase().trim());

  const filteredTemplates = templates.filter(t => {
    // Hide templates matching existing event names (for celebration entity type)
    if (entityType === 'celebration' && existingTitlesLower.includes(t.name.toLowerCase().trim())) {
      return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q)
    );
  });

  const allActivityOptions: ActivityTemplateOption[] = React.useMemo(() => {
    if (entityType !== 'event') {
      return [];
    }

    const activityByTemplateId = new Map<number, ActivityTemplateOption>();

    for (const template of filteredTemplates) {
      for (const activity of template.activities) {
        if (!activity.activity_template_id || !activity.activity_title) {
          continue;
        }

        if (!activityByTemplateId.has(activity.activity_template_id)) {
          activityByTemplateId.set(activity.activity_template_id, {
            id: activity.activity_template_id,
            title: activity.activity_title,
            description: activity.activity_description,
            sourceTemplateName: template.name,
          });
        }
      }
    }

    if (![...activityByTemplateId.values()].some((item) => item.title.toLowerCase() === MAIN_ACTIVITY_TEMPLATE.title.toLowerCase())) {
      activityByTemplateId.set(MAIN_ACTIVITY_TEMPLATE.id, {
        id: MAIN_ACTIVITY_TEMPLATE.id,
        title: MAIN_ACTIVITY_TEMPLATE.title,
        description: MAIN_ACTIVITY_TEMPLATE.description,
      });
    }

    return [...activityByTemplateId.values()];
  }, [entityType, filteredTemplates]);

  const filteredActivityOptions: ActivityTemplateOption[] = React.useMemo(() => {
    return allActivityOptions.filter((activity) => {
      if (!search.trim()) {
        return true;
      }

      const q = search.toLowerCase();
      return (`${activity.title} ${activity.description ?? ''} ${activity.sourceTemplateName ?? ''}`).toLowerCase().includes(q);
    });
  }, [allActivityOptions, search]);

  const isDirty = addedTemplateIds.size > 0;

  const toggleExpandSection = (templateId: number, section: ExpandSection) => {
    setExpandedSections(prev => {
      const current = prev[templateId];
      if (current === section) return { ...prev, [templateId]: null };
      return { ...prev, [templateId]: section };
    });
    setApplyError(null);
  };

  const handleAdd = (templateId: number) => {
    setAddedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (addedTemplateIds.size === 0) return;

    const templateIds = Array.from(addedTemplateIds);

    setApplying(true);
    setApplyError(null);

    let totalActivities = 0;
    let totalGoals = 0;
    let totalEvents = 0;

    try {
      if (entityType === 'event') {
        const selectedActivities = allActivityOptions.filter((option) => templateIds.includes(option.id));

        for (const activity of selectedActivities) {
          await activityService.createActivity(entityId, {
            title: activity.title,
            description: activity.description,
            status: 'planned',
          });
          totalActivities += 1;
        }

        const parts = totalActivities > 0
          ? [`${totalActivities} activit${totalActivities > 1 ? 'ies' : 'y'}`]
          : [];

        if (parts.length > 0) {
          setSuccessMessage(t('templatePicker.created', { items: parts.join(', ') }));
        } else {
          setSuccessMessage(t('templatePicker.alreadyExists'));
        }

        onClose();
        onApplied({ activities_created: totalActivities, goals_attached: 0, events_created: 0 });
        return;
      }

      for (const tplId of templateIds) {
        const template = templates.find(t => t.id === tplId);
        if (!template) continue;

        const result = await eventTemplateService.apply(tplId, {
          entity_type: entityType,
          entity_id: entityId,
        });
        totalActivities += result.activities_created;
        totalGoals += result.goals_attached;
        totalEvents += result.events_created || 0;
      }

      // Show inline success message and auto-close
      const parts: string[] = [];
      if (totalEvents > 0) parts.push(`${totalEvents} event${totalEvents > 1 ? 's' : ''}`);
      if (totalActivities > 0) parts.push(`${totalActivities} activit${totalActivities > 1 ? 'ies' : 'y'}`);
      if (totalGoals > 0) parts.push(`${totalGoals} goal${totalGoals > 1 ? 's' : ''}`);

      if (parts.length > 0) {
        setSuccessMessage(t('templatePicker.created', { items: parts.join(', ') }));
      } else {
        setSuccessMessage(t('templatePicker.alreadyExists'));
      }

      // Defer onApplied until auto-close fires — calling it immediately triggers
      // loadCelebrationData → setLoading(true) → parent unmounts us before the
      // success message can render.
      autoCloseTimer.current = setTimeout(() => {
        onClose();
        onApplied({ activities_created: totalActivities, goals_attached: totalGoals, events_created: totalEvents });
      }, 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('templatePicker.errors.applyTemplates');
      setApplyError(msg);
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-500" />
              {entityType === 'celebration' ? t('templatePicker.eventsTitle') : t('templatePicker.activitiesTitle')}
            </h3>
            <Button variant="ghost" size="icon-sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Search + Controls */}
          <div className="p-4 border-b shrink-0 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('templatePicker.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={onlyRelated}
                  onChange={e => setOnlyRelated(e.target.checked)}
                  className="rounded border-gray-300"
                />
                {t('templatePicker.onlyRelated')}
              </label>
              {(manualEntryUrl || onManualEntry) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (onManualEntry) {
                      onManualEntry();
                      return;
                    }
                    if (manualEntryUrl) {
                      onClose();
                      navigate(manualEntryUrl);
                    }
                  }}
                >
                  <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                  {t('templatePicker.enterManually')}
                </Button>
              )}
            </div>
          </div>

          {/* Templates List */}
          <div className="overflow-y-auto flex-1 p-4">
            {loading && (
              <div className="text-center py-8 text-muted-foreground">
                {t('templatePicker.loadingTemplates')}
              </div>
            )}

            {!loading && filteredTemplates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {search ? t('templatePicker.noMatch') : t('templatePicker.noTemplatesAvailable')}
              </div>
            )}

            {!loading && entityType === 'event' && filteredActivityOptions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {search ? t('templatePicker.noMatch') : 'No activities available for this event.'}
              </div>
            )}

            <div className="space-y-3">
              {entityType === 'event' && filteredActivityOptions.map((activityOption) => {
                const isAdded = addedTemplateIds.has(activityOption.id);

                return (
                  <div key={`activity-option-${activityOption.id}`}>
                    <EventSummaryCard
                      title={activityOption.title}
                      description={activityOption.description}
                      fallbackMetaText={activityOption.sourceTemplateName ? `From ${activityOption.sourceTemplateName}` : 'Generic'}
                      className={`${isAdded ? 'border-green-300 bg-green-50/30' : 'hover:border-indigo-300'} p-4`}
                      extraBadges={isAdded ? (
                        <Badge variant="active" className="text-xs">
                          <Check className="h-3 w-3 mr-0.5" />Added
                        </Badge>
                      ) : undefined}
                      trailing={(
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant={isAdded ? 'outline' : 'default'}
                            onClick={() => handleAdd(activityOption.id)}
                          >
                            {isAdded ? t('common:actions.remove') : t('common:actions.add')}
                          </Button>
                        </div>
                      )}
                    />
                  </div>
                );
              })}

              {entityType !== 'event' && filteredTemplates.map((template) => {
                const activities = template.activities;
                const goals = template.goals;
                const expandedSection = expandedSections[template.id] ?? null;
                const isAdded = addedTemplateIds.has(template.id);
                const showActivities = expandedSection === 'activities' || expandedSection === 'all';
                const showGoals = expandedSection === 'goals' || expandedSection === 'all';
                const isAnyExpanded = expandedSection !== null;

                return (
                  <div key={template.id}>
                    <EventSummaryCard
                      title={template.name}
                      description={template.description}
                      isSystemTemplate={template.is_system}
                      activityCount={activities.length}
                      goalCount={goals.length}
                      onActivityClick={() => toggleExpandSection(template.id, 'activities')}
                      onGoalClick={() => toggleExpandSection(template.id, 'goals')}
                      activityHighlighted={showActivities}
                      goalHighlighted={showGoals}
                      className={`${isAdded ? 'border-green-300 bg-green-50/30' : 'hover:border-indigo-300'} ${isAnyExpanded ? 'rounded-b-none border-b-0' : ''} p-4`}
                      extraBadges={isAdded ? (
                        <Badge variant="active" className="text-xs">
                          <Check className="h-3 w-3 mr-0.5" />Added
                        </Badge>
                      ) : undefined}
                      trailing={(
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleExpandSection(template.id, 'all')}
                          >
                            {isAnyExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant={isAdded ? 'outline' : 'default'}
                            onClick={() => handleAdd(template.id)}
                          >
                            {isAdded ? 'Remove' : 'Add'}
                            {isAdded ? t('common:actions.remove') : t('common:actions.add')}
                          </Button>
                        </div>
                      )}
                    />

                    {/* Expanded Details */}
                    {isAnyExpanded && (
                      <div className="rounded-b-lg border border-t-0 bg-gray-50 p-4 space-y-4">
                        {/* Activities Preview */}
                        {showActivities && activities.length > 0 && (
                          <div>
                            <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <List className="h-4 w-4 text-indigo-500" />
                              Activities ({activities.length})
                            </h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {activities.slice(0, 10).map((activity) => (
                                <div
                                  key={activity.id}
                                  className="text-sm bg-white p-2 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    {activity.activity_title}
                                  </div>
                                  {activity.activity_description && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {activity.activity_description}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                    {activity.duration_minutes && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {activity.duration_minutes} min
                                        {t('templatePicker.minutes', { count: activity.duration_minutes })}
                                      </span>
                                    )}
                                    {activity.parallel_group && (
                                      <Badge variant="outline" className="text-xs">
                                        {activity.parallel_group}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {activities.length > 10 && (
                                <div className="text-xs text-center text-muted-foreground py-2">
                                  {t('templatePicker.moreActivities', { count: activities.length - 10 })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Goals Preview */}
                        {showGoals && goals.length > 0 && (
                          <div>
                            <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <Target className="h-4 w-4 text-green-500" />
                              Linked Goals ({goals.length})
                            </h5>
                            <div className="space-y-1">
                              {goals.map((goal) => (
                                <div
                                  key={goal.id}
                                  className="text-sm bg-white p-2 rounded border flex items-center gap-2"
                                >
                                  <Target className="h-3 w-3 text-green-500 shrink-0" />
                                  <span className="text-gray-900">
                                    {goal.goal_name || `Goal #${goal.goal_template_id}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Show message if section has no items */}
                        {showActivities && activities.length === 0 && (
                          <p className="text-sm text-muted-foreground italic">{t('templatePicker.noActivitiesInTemplate')}</p>
                        )}
                        {showGoals && goals.length === 0 && (
                          <p className="text-sm text-muted-foreground italic">{t('templatePicker.noGoalsInTemplate')}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Apply Error */}
            {applyError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {applyError}
              </div>
            )}
          </div>

          {/* Footer with Save */}
          <div className="p-4 border-t shrink-0 bg-gray-50 space-y-3">
            {/* Inline success/warning message */}
            {successMessage && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                successMessage.startsWith('All events') 
                  ? 'bg-amber-50 border border-amber-200 text-amber-700'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}>
                {successMessage.startsWith('All events')
                  ? <AlertTriangle className="h-4 w-4 shrink-0" />
                  : <CheckCircle2 className="h-4 w-4 shrink-0" />
                }
                {successMessage}
              </div>
            )}
            <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {isDirty ? `${addedTemplateIds.size} template${addedTemplateIds.size > 1 ? 's' : ''} selected` : 'Select templates to add'}
              {isDirty ? t('templatePicker.selectedCount', { count: addedTemplateIds.size }) : t('templatePicker.selectTemplates')}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!isDirty || applying || !!successMessage}
              >
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {t('common:status.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-1.5" />
                    {t('common:actions.save')}
                  </>
                )}
              </Button>
            </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};
